/**
 * 🗺️ The Wilds — Mines V2 frontend
 *
 * 4-quadrant world map (N/S/E/W). Only SOUTH is active at launch.
 * Players claim CPU-controlled veins (free, first-come-first-serve), own them
 * for life or until beaten in PvP, and abandon them back to CPU at will.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WildsPartyCombat from "@/components/WildsPartyCombat";
import { useCombatSounds, CombatSoundToggle } from "@/hooks/useCombatSounds";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🏰 Tiny castle-state hook — surfaces "Mini Castles" awareness inside Wilds.
function useCastleSnapshot(authed, quadrantId) {
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    if (!authed) return;
    let cancel = false;
    (async () => {
      try {
        const [stateR, mapR] = await Promise.all([
          axios.get(`${API}/castle/state`),
          axios.get(`${API}/castle/map`, { params: { quadrant: quadrantId || "south", page: 0, page_size: 6 } }),
        ]);
        if (!cancel) setSnap({ state: stateR.data, map: mapR.data });
      } catch {/* swallow — castles surfacing is optional */}
    })();
    return () => { cancel = true; };
  }, [authed, quadrantId]);
  return snap;
}

// 🗺️ Aethelgard quadrant labels — locked by owner canon Sep 2, 2026.
// Full lore lives in /app/memory/AETHELGARD_WORLD_MAP_QUADRANTS_SEP2026.md.
// Aethelgard is the WORLD. Each quadrant has its own region name below.
const QUADRANT_META = {
  south: { label: "Dragonious Castle",      emoji: "🏰", flavor: "Small kingdom, giant anomalies. Time loops and spatial distortions bloom here." },
  north: { label: "Tree of Potential",      emoji: "🌲", flavor: "Northern hub where Ultimate gear ascends through 5 states of Flux." },
  east:  { label: "The Astral Aquifer",     emoji: "💧", flavor: "Divine wading pools the Sky-Phoenixes left behind. Home of the Healing Mages." },
  west:  { label: "The Ash-Bound Horizon",  emoji: "🌋", flavor: "The sun rests here in a continental nest of embers. The Pyre-Lands." },
};


export default function WildsPage() {
  const { isAuthed, user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  // 🏰 Castle snapshot — shows the player their castle + neighbours on the Wilds.
  const castleSnap = useCastleSnapshot(isAuthed, "south");
  const [busy, setBusy] = useState(false);
  const [openMineId, setOpenMineId] = useState(null);
  // 🗺️⚔️ Wilds monster battles V1
  const [hunt, setHunt] = useState(null);          // {refresh_id, mobs, seconds_to_refresh}
  const [huntMob, setHuntMob] = useState(null);    // mob currently being inspected/attacked
  const [battleResult, setBattleResult] = useState(null);  // last battle outcome
  // 👥 Wilds Hunting Party V1
  const [party, setParty] = useState(null);
  const [partyInvites, setPartyInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  // 👥⚔️ Party Combat V2 — active encounter overlay
  const [encounterId, setEncounterId] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/wilds/state`);
      setState(r.data);
      // If we got mine income, surface it as a soft toast
      const settle = r.data.last_settle;
      if (settle && settle.produced > 0) {
        toast.success(
          `⛏️ ${settle.produced.toLocaleString()} 🪙 mined while you were away`,
          { duration: 3500 },
        );
      }
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Failed to load the Wilds.");
    }
    try {
      const rh = await axios.get(`${API}/wilds/battle/state`);
      setHunt(rh.data);
    } catch (e) {
      // Non-fatal — mob hunt may not be wired yet
    }
    try {
      const rp = await axios.get(`${API}/party/state`);
      setParty(rp.data?.party || null);
      setPartyInvites(rp.data?.pending_invites || []);
    } catch (e) {
      // Non-fatal — party may not exist
    }
    // 👥⚔️ Auto-resume active party encounter if any
    try {
      const re = await axios.get(`${API}/encounter/active`);
      const enc = re.data?.encounter;
      if (enc?.encounter_id && enc?.status === "active") {
        setEncounterId(enc.encounter_id);
      }
    } catch (e) {
      // Non-fatal — no active encounter
    }
  };

  useEffect(() => {
    if (!isAuthed) return;
    refresh();
    // Auto-settle every 60s while on the page
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [isAuthed]);

  const onClaim = async (mine_id, vein_idx) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/wilds/claim-vein`, { mine_id, vein_idx });
      toast.success(`🚩 Claimed Vein #${vein_idx} · +720 🪙/hr`);
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setBusy(false);
    }
  };

  const onAbandon = async (mine_id, vein_idx) => {
    if (!window.confirm(`Abandon Vein #${vein_idx}? It will revert to CPU control and stop producing for you.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/wilds/abandon-vein`, { mine_id, vein_idx });
      toast.success(`🏳️ Abandoned Vein #${vein_idx}.`);
      setState(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Abandon failed.");
    } finally {
      setBusy(false);
    }
  };

  // 👥 Party action handlers
  const onPartyCreate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/party/create`, {});
      toast.success("🐦 Hunting Party formed!");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't form party.");
    } finally { setBusy(false); }
  };
  const onPartyLeave = async () => {
    if (!window.confirm("Leave your party?")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/party/leave`, {});
      if (r.data?.dissolved) toast.success("Party dissolved.");
      else if (r.data?.leadership_transferred_to) toast.success("Leadership transferred — you're out.");
      else toast.success("You left the party.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't leave.");
    } finally { setBusy(false); }
  };
  const onPartyDissolve = async () => {
    if (!window.confirm("Dissolve the party? Everyone gets ejected.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/party/dissolve`, {});
      toast.success("Party dissolved.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't dissolve.");
    } finally { setBusy(false); }
  };
  const onPartyKick = async (member_id) => {
    if (!window.confirm("Kick this member?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/party/kick`, { member_id });
      toast.success("Kicked.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't kick.");
    } finally { setBusy(false); }
  };
  const onAcceptInvite = async (party_id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/party/accept`, { party_id });
      toast.success("🐦 Joined the party!");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept.");
    } finally { setBusy(false); }
  };
  const onDeclineInvite = async (party_id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/party/decline`, { party_id });
      toast.success("Declined.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't decline.");
    } finally { setBusy(false); }
  };

  // ⚔️ Attack a wilds mob (one-shot battle)
  const onAttackMob = async (mob) => {
    setBusy(true);
    setBattleResult(null);
    try {
      const r = await axios.post(`${API}/wilds/battle/attack`, { instance_id: mob.instance_id });
      setBattleResult(r.data);
      setHunt(r.data.state);
      if (r.data.winner === "pet") {
        const d = r.data.drops || {};
        let msg = `⚔️ Slain! +${d.chits || 0} 🪙`;
        if (d.pet_xp) msg += ` · +${d.pet_xp} XP`;
        if (d.gear) msg += ` · 🎁 ${d.gear.name}`;
        if (d.material) msg += ` · ${d.material.icon} ${d.material.name}`;
        toast.success(msg, { duration: 4500 });
      } else {
        toast.error(`💀 You were beaten by the ${mob.name}. Rest and try again.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attack failed.");
    } finally {
      setBusy(false);
    }
  };

  // ⚔️🎯 Manual turn-based fight — engages the same mob but lets the player
  // pick a skill every turn (Feb 2026 combat pivot). Routes to /battle.
  // If a stale/unfinished manual battle exists, offer to forfeit + retry so
  // the player isn't stuck (common cause of "Couldn't engage the mob").
  const onAttackMobManual = async (mob) => {
    setBusy(true);
    const start = async () => {
      await axios.post(`${API}/wilds/battle/start-manual`, { instance_id: mob.instance_id });
      toast.success(`⚔️ Engaging ${mob.name}...`);
      navigate("/battle?return_to=/wilds");
    };
    try {
      await start();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't engage the mob.";
      if (detail.includes("already have a battle")) {
        // Peek at the stale battle so the prompt isn't blind.
        let staleLabel = "an unfinished fight";
        try {
          const s = await axios.get(`${API}/manual-battle/state`);
          const b = s?.data?.battle;
          if (b) {
            const eName = b?.enemy?.name || "an opponent";
            const ctx = b?.context || "";
            staleLabel = `your unfinished fight vs ${eName}${ctx ? ` (${ctx})` : ""}`;
          }
        } catch { /* non-fatal */ }
        const forfeit = window.confirm(
          `You still have ${staleLabel}.\n\n` +
          `OK  →  Forfeit it and engage ${mob.name} now.\n` +
          `Cancel  →  Resume the old fight first.`,
        );
        if (forfeit) {
          try {
            await axios.post(`${API}/manual-battle/forfeit`, {});
            await start();
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Couldn't forfeit the old fight.");
          }
        } else {
          toast.message("⚔️ Resuming your unfinished fight…");
          navigate("/battle?return_to=/wilds");
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  // 👥⚔️ Party hunt — leader-only, kicks off a real-time encounter
  const onPartyHuntMob = async (mob) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/wilds/battle/party-start`, {
        mob_instance_id: mob.instance_id,
      });
      const enc = r.data;
      if (enc?.encounter_id) {
        setEncounterId(enc.encounter_id);
        setHuntMob(null);   // close the inspect modal so overlay takes over
        toast.success("⚔️ Party engaged! Take your turn.", { duration: 3500 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start party fight.");
    } finally {
      setBusy(false);
    }
  };

  // 👥⚔️ Close party combat overlay (only allowed once resolved)
  const onCloseEncounter = async () => {
    setEncounterId(null);
    // Refresh hunt + party + wallet to reflect drops
    await refresh();
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0010] via-black to-[#0a0010] text-amber-100">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="text-6xl mb-4">🗺️</div>
          <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            THE WILDS
          </h1>
          <p className="text-neutral-400">Sign in to explore.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0010] via-black to-[#1a0a00] text-amber-100" data-testid="wilds-page">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Hero banner */}
        <div className="rounded-2xl border border-amber-700/40 bg-gradient-to-br from-[#1a0500] via-[#0a0010] to-black p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 text-[140px] opacity-10 select-none">🗺️</div>
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.4em] text-amber-500/70 mb-1">Kingdom of Dragonious</div>
            <h1 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🗺️ THE WILDS
            </h1>
            <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
              Beyond the city walls, the soot drifts thick. Ancient mines pierce the earth. Stake your claim — only the strong hold their ground.
            </p>

            {state && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Stat label="Owned Veins" value={`${state.owned_vein_count} / ${state.max_owned_veins}`}
                      sub={state.next_vein_unlock ? `Next @ Pet Lv ${state.next_vein_unlock.at_pet_level}` : "Cap unlocked"}
                      testid="wilds-owned-count" />
                <Stat label="Pet Lv" value={state.pet_level} sub="gates ownership" testid="wilds-pet-lv" />
                <Stat label="Rate" value={`${(state.owned_vein_count * (state.vein_rate_per_hr || 720)).toLocaleString()} 🪙/hr`}
                      sub="passive earnings" testid="wilds-rate" />
                <Stat label="🌾 Safe" value={(state.wallet?.grainhouse_chits || 0).toLocaleString()}
                      sub={`🪙 ${(state.wallet?.chits || 0).toLocaleString()} overflow`} testid="wilds-wallet" />
              </div>
            )}
          </div>
        </div>

        {/* 4-Quadrant Map */}
        <div className="grid grid-cols-2 gap-3 mb-8" data-testid="wilds-quadrant-map">
          {["north", "east", "west", "south"].map((q) => {
            const quad = state?.quadrants?.[q];
            const isActive = quad?.active;
            const meta = QUADRANT_META[q];
            return (
              <div key={q}
                   className={`rounded-xl border p-4 transition ${
                     isActive
                       ? "border-amber-600/50 bg-gradient-to-br from-amber-950/30 to-black/40 cursor-pointer hover:border-amber-500"
                       : "border-neutral-900 bg-black/40 grayscale opacity-50"
                   }`}
                   data-testid={`wilds-quadrant-${q}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400/70">{meta.label}</div>
                    <div className="text-2xl font-black text-amber-200">{meta.emoji} {meta.label}</div>
                  </div>
                  <div className={`text-[10px] px-2 py-0.5 rounded-full font-black tracking-wider ${
                    isActive ? "bg-amber-500 text-black" : "bg-neutral-800 text-neutral-500"
                  }`}>
                    {isActive ? "ACTIVE" : "LOCKED"}
                  </div>
                </div>
                <div className="text-xs text-neutral-500 mt-1">{meta.flavor}</div>
                {isActive && quad.mines.length > 0 && (
                  <div className="text-xs text-amber-300 mt-2">⛏️ {quad.mines.length} mines · {quad.mines.reduce((sum, m) => sum + m.veins_total, 0)} veins</div>
                )}
              </div>
            );
          })}
        </div>

        {/* South Quadrant — Mines */}
        {state && state.quadrants.south.active && (
          <section data-testid="wilds-south-section">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
                🌋 SOUTH QUADRANT — ACTIVE MINES
              </h2>
              <div className="h-px flex-1 bg-gradient-to-r from-amber-600/60 to-transparent" />
            </div>

            {/* 🏰 Kingdom of Dragonious Castle — landmark / return-to-city */}
            <Link
              to="/"
              data-testid="wilds-castle-return"
              className="block mb-5 group"
              title="Return to the Kingdom of Dragonious"
            >
              <div className="relative overflow-hidden rounded-2xl border border-amber-600/40 bg-gradient-to-r from-[#1a0033] via-[#0a0010] to-[#1a0500] p-5 transition-all hover:border-amber-400/70 hover:shadow-[0_0_24px_rgba(245,158,11,0.25)]">
                <div className="absolute -right-6 -top-6 text-[120px] opacity-10 select-none group-hover:opacity-20 transition-opacity">🏰</div>
                <div className="relative flex items-center gap-4">
                  <div className="text-5xl drop-shadow-[0_0_8px_rgba(245,158,11,0.4)]">🏰</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-[10px] uppercase tracking-[0.35em] text-amber-400/70">Landmark</div>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-900/60 border border-purple-700/50 text-purple-200 font-black tracking-wider">PLACEHOLDER</span>
                    </div>
                    <div className="text-xl md:text-2xl font-black text-amber-100 leading-tight" style={{ fontFamily: "Anton, sans-serif" }}>
                      KINGDOM OF DRAGONIOUS CASTLE
                    </div>
                    <div className="text-xs text-neutral-400 mt-1">
                      🛡️ The city gates rise on the horizon. Return to Dragonious.
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1 text-amber-300 group-hover:text-amber-200 transition-colors text-sm font-bold">
                    Enter
                    <span className="text-base transition-transform group-hover:translate-x-1">→</span>
                  </div>
                </div>
              </div>
            </Link>

            {/* 👥 Wilds Hunting Party V1 */}
            <PartyPanel
              party={party}
              invites={partyInvites}
              busy={busy}
              onCreate={onPartyCreate}
              onLeave={onPartyLeave}
              onDissolve={onPartyDissolve}
              onKick={onPartyKick}
              onAccept={onAcceptInvite}
              onDecline={onDeclineInvite}
              onInviteClick={() => setShowInviteModal(true)}
            />

            {/* 🗺️⚔️ Monster Hunt — mobs near the Castle */}
            {hunt && (
              <MonsterHuntPanel
                hunt={hunt}
                busy={busy}
                onInspect={(m) => setHuntMob(m)}
              />
            )}

            {/* 🏰 Mini Castles — owner-locked Feb 2026. Surfaces the Castle
                system right where players expect to find it: ON the Wilds map.
                Click-through goes to the full /castle page for claim/raid. */}
            <WildsCastlesPanel snap={castleSnap} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {state.quadrants.south.mines.map((mine) => (
                <MineCard key={mine.mine_id}
                          mine={mine}
                          onOpen={() => setOpenMineId(mine.mine_id)} />
              ))}
            </div>
          </section>
        )}

        {/* Mine drill-down modal */}
        {openMineId && state && (
          <MineModal
            mine={state.quadrants.south.mines.find((m) => m.mine_id === openMineId)}
            state={state}
            busy={busy}
            onClose={() => setOpenMineId(null)}
            onClaim={onClaim}
            onAbandon={onAbandon}
          />
        )}

        {/* 🗺️⚔️ Monster confirm/attack modal */}
        {huntMob && (
          <MonsterConfirmModal
            mob={huntMob}
            busy={busy}
            party={party}
            result={battleResult && battleResult.instance_id === huntMob.instance_id ? battleResult : null}
            onClose={() => { setHuntMob(null); setBattleResult(null); }}
            onAttack={() => onAttackMob(huntMob)}
            onAttackManual={() => onAttackMobManual(huntMob)}
            onPartyHunt={() => onPartyHuntMob(huntMob)}
          />
        )}

        {/* 👥⚔️ Party Combat Overlay (real-time polling) */}
        {encounterId && (
          <WildsPartyCombat
            encounterId={encounterId}
            viewerUserId={user?.user_id}
            isLeader={party?.members?.some((m) => m.is_viewer && m.role === "leader") || false}
            onClose={onCloseEncounter}
          />
        )}

        {/* 👥 Invite a friend to the party */}
        {showInviteModal && party && (
          <InviteFriendModal
            party={party}
            onClose={() => setShowInviteModal(false)}
            onInvited={refresh}
          />
        )}
      </main>

      <Footer />
    </div>
  );
}


function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-amber-900/50 bg-black/40 p-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-amber-500/70">{label}</div>
      <div className="text-lg font-black text-amber-200">{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}


function MineCard({ mine, onOpen }) {
  const claimedCount = (mine.veins || []).filter((v) => !v.is_unclaimed).length;
  const myCount = (mine.veins || []).filter((v) => v.is_mine).length;
  const pct = Math.round((claimedCount / mine.veins_total) * 100);
  return (
    <button onClick={onOpen}
            data-testid={`wilds-mine-${mine.mine_id}`}
            className="rounded-xl border border-amber-700/40 bg-gradient-to-br from-[#1a0500] to-black/60 p-5 text-left hover:border-amber-500 hover:shadow-[0_0_20px_rgba(245,158,11,0.15)] transition-all">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-3xl">{mine.icon}</div>
          <div className="text-lg font-black text-amber-200 mt-1">{mine.name}</div>
        </div>
        {myCount > 0 && (
          <div className="px-2 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-black"
               data-testid={`wilds-mine-mine-${mine.mine_id}-mycount`}>
            YOU OWN {myCount}
          </div>
        )}
      </div>

      <div className="mt-3 text-xs text-neutral-500 leading-snug line-clamp-2">{mine.mob.lore}</div>

      <div className="mt-3 flex items-center gap-2 text-xs">
        <span className="text-amber-300">{mine.mob.icon} {mine.mob.name}</span>
        <span className="text-neutral-600">·</span>
        <span className="text-neutral-400">Lv {mine.mob.lv_range[0]}-{mine.mob.lv_range[1]}</span>
      </div>

      {/* Claim progress bar */}
      <div className="mt-3">
        <div className="flex justify-between text-[10px] text-neutral-500 mb-1">
          <span>{claimedCount}/{mine.veins_total} claimed</span>
          <span>{mine.veins_unclaimed} open</span>
        </div>
        <div className="h-2 rounded-full bg-neutral-900 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all"
               style={{ width: `${pct}%` }} />
        </div>
      </div>
    </button>
  );
}


function MineModal({ mine, state, busy, onClose, onClaim, onAbandon }) {
  if (!mine) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="wilds-mine-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="relative w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-2xl border border-amber-700/40 bg-gradient-to-br from-[#1a0500] to-black p-6">
        <button onClick={onClose}
                data-testid="wilds-modal-close"
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-amber-500/30 flex items-center justify-center text-amber-300 text-lg">
          ×
        </button>

        <div className="flex items-start gap-4 mb-4">
          <div className="text-5xl">{mine.icon}</div>
          <div className="flex-1">
            <div className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              {mine.name}
            </div>
            <div className="text-xs text-neutral-500 mt-0.5">
              🌋 South Quadrant · 10 veins · 720 🪙/hr each
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-black/40 border border-neutral-800 p-3 mb-4 text-sm">
          <div className="font-bold text-amber-300 mb-1">{mine.mob.icon} {mine.mob.name} · Lv {mine.mob.lv_range[0]}-{mine.mob.lv_range[1]}</div>
          <div className="text-xs text-neutral-400 italic leading-snug">{mine.mob.lore}</div>
        </div>

        <div className="text-xs text-neutral-500 mb-2">
          You can own <span className="text-amber-300 font-bold">{state.max_owned_veins}</span> vein(s)
          total at Pet Lv {state.pet_level}.
          {state.next_vein_unlock && (
            <> Next slot unlocks at Pet Lv {state.next_vein_unlock.at_pet_level}.</>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {mine.veins.map((v) => (
            <VeinTile key={v.idx}
                      mine_id={mine.mine_id}
                      vein={v}
                      busy={busy}
                      canClaim={state.owned_vein_count < state.max_owned_veins}
                      onClaim={onClaim}
                      onAbandon={onAbandon} />
          ))}
        </div>
      </div>
    </div>
  );
}


function VeinTile({ mine_id, vein, busy, canClaim, onClaim, onAbandon }) {
  const status = vein.is_mine ? "mine" : vein.is_unclaimed ? "open" : "owned";
  const ringClass = {
    mine:  "border-amber-500 bg-amber-500/15 shadow-[0_0_12px_rgba(245,158,11,0.4)]",
    open:  "border-amber-700/40 bg-black/40 hover:border-amber-500 cursor-pointer",
    owned: "border-red-900/40 bg-black/30 grayscale opacity-70",
  }[status];
  return (
    <div data-testid={`wilds-vein-${mine_id}-${vein.idx}`}
         className={`rounded-lg border p-3 text-center text-xs transition ${ringClass}`}>
      <div className="text-2xl">{status === "mine" ? "⛏️" : status === "open" ? "💎" : "🔒"}</div>
      <div className="font-black text-amber-200 mt-1">Vein #{vein.idx}</div>
      <div className="text-[10px] text-neutral-500">Mob Lv {vein.mob_lv}</div>
      {status === "owned" && (
        <div className="text-[10px] text-red-300 mt-1 truncate" data-testid={`wilds-vein-${mine_id}-${vein.idx}-owner`}>
          @{vein.owner_handle}
        </div>
      )}
      {status === "mine" && (
        <button onClick={() => onAbandon(mine_id, vein.idx)}
                disabled={busy}
                data-testid={`wilds-vein-${mine_id}-${vein.idx}-abandon`}
                className="mt-2 w-full px-2 py-1 rounded bg-red-900/60 hover:bg-red-900/80 disabled:opacity-50 text-red-200 text-[10px] font-bold">
          🏳️ Abandon
        </button>
      )}
      {status === "open" && (
        <button onClick={() => onClaim(mine_id, vein.idx)}
                disabled={busy || !canClaim}
                data-testid={`wilds-vein-${mine_id}-${vein.idx}-claim`}
                className="mt-2 w-full px-2 py-1 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black text-[10px] font-bold">
          {canClaim ? "🚩 Claim" : "🔒 Full"}
        </button>
      )}
    </div>
  );
}



/* ───────── 🗺️⚔️ Wilds Monster Hunt V1 ───────── */

function formatCountdown(sec) {
  if (sec == null || sec < 0) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

function MonsterHuntPanel({ hunt, busy, onInspect }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const secondsLeft = Math.max(0, (hunt.seconds_to_refresh || 0) - tick);
  const mobs = hunt.mobs || [];
  const aliveCount = mobs.filter((m) => m.status === "alive").length;
  const slainCount = mobs.filter((m) => m.status === "dead").length;

  return (
    <div
      className="relative rounded-2xl border border-red-800/50 bg-gradient-to-br from-[#1a0010] via-black to-[#1a0500] p-5 mb-5"
      data-testid="wilds-hunt-panel"
    >
      <div className="absolute -top-6 -right-6 text-[110px] opacity-10 select-none">⚔️</div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-red-400/70">Castle Approach</div>
          <h2 className="text-2xl md:text-3xl font-black text-red-200" style={{ fontFamily: "Anton, sans-serif" }}>
            🗡️ MONSTER HUNT
          </h2>
          <div className="text-xs text-neutral-400 mt-1">
            Tap a monster to inspect, confirm, and engage. Drops scale to the threat.
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="rounded-md border border-red-900/50 bg-black/40 px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wider text-red-400/70">Refresh</div>
            <div className="font-black text-red-200" data-testid="wilds-hunt-refresh">
              ⏰ {formatCountdown(secondsLeft)}
            </div>
          </div>
          <div className="rounded-md border border-amber-900/50 bg-black/40 px-2 py-1.5">
            <div className="text-[9px] uppercase tracking-wider text-amber-400/70">Alive / Slain</div>
            <div className="font-black text-amber-200">
              {aliveCount} / {slainCount}
            </div>
          </div>
        </div>
      </div>

      {/* Map overlay — mobs positioned by x/y on a "wilds map" background */}
      <div
        className="relative w-full h-72 rounded-xl border border-amber-900/40 overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse at center, #1a0a02 0%, #0a0500 60%, #000 100%), repeating-linear-gradient(90deg, rgba(245,158,11,0.04) 0 1px, transparent 1px 24px)",
        }}
        data-testid="wilds-hunt-map"
      >
        {/* Castle marker */}
        <div className="absolute" style={{ left: "2%", top: "8%" }}>
          <div className="text-2xl drop-shadow-[0_0_6px_rgba(245,158,11,0.5)]">🏰</div>
          <div className="text-[9px] text-amber-300/70 -mt-1 font-bold">CASTLE</div>
        </div>

        {mobs.map((m) => (
          <button
            key={m.instance_id}
            onClick={() => onInspect(m)}
            disabled={busy || m.status !== "alive"}
            data-testid={`wilds-mob-${m.instance_id}`}
            className={`absolute -translate-x-1/2 -translate-y-1/2 transition-all ${
              m.status === "alive"
                ? "hover:scale-125 cursor-pointer"
                : "grayscale opacity-30 cursor-not-allowed"
            }`}
            style={{ left: `${m.x}%`, top: `${m.y}%` }}
            title={`${m.name} · Lv ${m.lv}${m.status !== "alive" ? " (slain)" : ""}`}
          >
            <div className={`text-3xl ${m.is_map_boss ? "drop-shadow-[0_0_12px_rgba(220,38,38,0.7)]" : ""}`}>
              {m.icon}
            </div>
            <div className="text-[9px] font-black text-amber-200 -mt-1 whitespace-nowrap">
              Lv {m.lv}{Number(m.pack_size || 1) > 1 ? ` · ×${m.pack_size}` : ""}
            </div>
            {m.is_map_boss && m.status === "alive" && (
              <div className="text-[8px] tracking-wider text-red-400 -mt-0.5 font-black">BOSS</div>
            )}
            {Number(m.pack_size || 1) > 1 && m.status === "alive" && !m.is_map_boss && (
              <div className="text-[8px] tracking-wider text-sky-300 -mt-0.5 font-black">PACK</div>
            )}
          </button>
        ))}
      </div>

      <div className="text-[10px] text-neutral-500 mt-2 text-center italic">
        🤝 Hidden: bring 2-4 heroes (Cari hints…) — drops upgrade in tier
      </div>
    </div>
  );
}


function MonsterConfirmModal({ mob, busy, party, result, onClose, onAttack, onAttackManual, onPartyHunt }) {
  const w = result?.winner;
  const drops = result?.drops;
  // 💠 Owner canon Feb 2026: ANY active party member can start an encounter,
  // not just the leader. The button is shown whenever the viewer is a party
  // member with at least themselves in the roster.
  const isInParty = party?.members?.some((m) => m.is_viewer) || false;
  const partyReady = isInParty && (party?.member_count || 1) >= 1;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="wilds-mob-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl border border-red-700/50 bg-gradient-to-br from-[#1a0010] to-black p-6"
      >
        <button
          onClick={onClose}
          data-testid="wilds-mob-modal-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-red-500/30 flex items-center justify-center text-red-300 text-lg"
        >
          ×
        </button>

        <div className="text-center mb-4">
          {/* 🐺 Feb 2026 — Pack preview: show one icon per member so
              a Pack ×4 reads as 4 enemies, not one. (owner directive) */}
          {Number(mob.pack_size || 1) > 1 ? (
            <div className="flex items-center justify-center gap-0 -space-x-4 mb-2" data-testid="wilds-mob-pack-icons">
              {Array.from({ length: Math.min(Number(mob.pack_size || 1), 6) }).map((_, i) => (
                <span key={i} className="text-6xl leading-none drop-shadow-lg"
                  style={{ transform: `translateY(${(i % 2) * 6}px) rotate(${(i - 1.5) * 4}deg)` }}>
                  {mob.icon}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-6xl mb-2">{mob.icon}</div>
          )}
          <div className="text-2xl font-black text-red-200" style={{ fontFamily: "Anton, sans-serif" }}>
            {mob.name}
          </div>
          <div className="text-xs text-neutral-400 mt-1">
            Lv {mob.lv}
            {Number(mob.pack_size || 1) > 1 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-900/60 text-amber-200 text-[10px] font-black tracking-wider" data-testid="wilds-mob-pack-badge">
                PACK ×{mob.pack_size}
              </span>
            )}
            {mob.is_map_boss && <span className="ml-2 px-2 py-0.5 rounded-full bg-red-900/60 text-red-200 text-[10px] font-black tracking-wider">MAP BOSS</span>}
          </div>
          <div className="text-xs text-neutral-500 italic mt-2">{mob.tagline}</div>
        </div>

        {!result && (
          <>
            <div className="rounded-lg bg-black/40 border border-red-900/40 p-3 mb-4 text-xs text-neutral-400">
              <div className="font-bold text-amber-200 mb-1">⚔️ Engage this monster?</div>
              <div>The fight resolves immediately. Win → 🪙 Chits + chance at gear/materials/XP. Lose → no penalty, mob still standing.</div>
            </div>
            <button
              onClick={onAttack}
              disabled={busy}
              data-testid="wilds-mob-attack"
              className="w-full py-3 rounded-lg bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black tracking-wider text-lg"
            >
              {busy ? "⚔️ FIGHTING…" : "⚔️ SOLO ATTACK"}
            </button>

            {/* ⚔️🎯 Manual turn-based fight (Feb 2026 combat pivot) */}
            {onAttackManual && (
              <button
                onClick={onAttackManual}
                disabled={busy}
                data-testid="wilds-mob-attack-manual"
                className="w-full mt-2 py-3 rounded-lg bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black tracking-wider text-base shadow-lg shadow-amber-900/40"
              >
                {busy ? "⏳ ENGAGING…" : "🎯 FIGHT MANUALLY"}
                <div className="text-[10px] font-normal opacity-80 mt-0.5">Pick every skill · Turn-by-turn · Full damage model</div>
              </button>
            )}

            {/* 👥⚔️ Party hunt — leader only, turn-by-turn polling combat */}
            {partyReady && (
              <button
                onClick={onPartyHunt}
                disabled={busy}
                data-testid="wilds-mob-party-hunt"
                className="w-full mt-2 py-3 rounded-lg bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 hover:from-indigo-500 hover:to-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-black tracking-wider text-base shadow-lg shadow-indigo-900/40"
              >
                {busy ? "⏳ ENGAGING…" : `👥 PARTY HUNT (${party.member_count}/${party.max_members})`}
                <div className="text-[10px] font-normal opacity-80 mt-0.5">Turn-by-turn · See every strike · Tier-bonus loot</div>
              </button>
            )}
            {!partyReady && party && (
              <div className="mt-2 text-[10px] text-indigo-300/70 italic text-center">
                👥 Join a Hunting Party above to launch a party hunt.
              </div>
            )}
            {!party && (
              <div className="mt-2 text-[10px] text-indigo-300/50 italic text-center">
                👥 Form a Hunting Party above to fight side-by-side and SEE every turn.
              </div>
            )}
          </>
        )}

        {result && (
          <SoloBattleReplay result={result} mob={mob} onClose={onClose} />
        )}
      </div>
    </div>
  );
}


/* ───────── 🎬 Solo Battle Animated Replay ───────── */

function SoloBattleReplay({ result, mob, onClose }) {
  const w = result?.winner;
  const drops = result?.drops;
  const log = (result?.log || []).slice(0, 50);  // safety cap so super-long fights don't stall
  const [revealed, setRevealed] = useState(0);
  const [petHp, setPetHp] = useState(result?.pet_stats?.max_hp || 100);
  const [mobHp, setMobHp] = useState(result?.cpu_stats?.max_hp || 100);
  const petMax = result?.pet_stats?.max_hp || 100;
  const mobMax = result?.cpu_stats?.max_hp || 100;
  const { play } = useCombatSounds();
  const playedFinaleRef = useRef(false);

  // Step through the log entries with a delay (skipped entirely if no log)
  useEffect(() => {
    if (log.length === 0) return;          // nothing to animate → drops show immediately
    if (revealed >= log.length) return;
    const id = setTimeout(() => {
      const entry = log[revealed];
      // 🔊 Sound: play hit SFX (crit overlay if it was a crit) per entry type
      if (entry?.dmg && (entry.type === "pet_attack" || entry.type === "cpu_attack" ||
                         entry.type === "mob_attack" || entry.actor)) {
        const isHeavy = entry.move === "heavy" || (entry.dmg || 0) >= 20;
        play(isHeavy ? "hit_heavy" : "hit_light");
        if (entry.crit) {
          // Layer crit ping slightly later so it doesn't muddy the hit SFX
          setTimeout(() => play("crit", { volume: 0.55 }), 80);
        }
      }
      // Update HP bars based on damage entries (pet | cpu | mob actor strings)
      if (entry?.dmg) {
        const a = (entry.actor || "").toLowerCase();
        if (a === "pet" || a === "phoenix") {
          setMobHp((h) => Math.max(0, h - (entry.dmg || 0)));
        } else if (a === "cpu" || a === "mob") {
          setPetHp((h) => Math.max(0, h - (entry.dmg || 0)));
        }
      }
      setRevealed((r) => r + 1);
    }, 550);
    return () => clearTimeout(id);
  }, [revealed, log, play]);

  // 🔊 Victory / Defeat stinger when reel finishes
  useEffect(() => {
    if (log.length > 0 && revealed < log.length) return;
    if (playedFinaleRef.current) return;
    playedFinaleRef.current = true;
    setTimeout(() => play(w === "pet" ? "victory" : "defeat", { volume: 0.55 }), 300);
  }, [revealed, log.length, w, play]);

  const skipToEnd = () => setRevealed(log.length);
  const playing = log.length > 0 && revealed < log.length;
  const petHpPct = Math.max(0, Math.min(100, (petHp / petMax) * 100));
  const mobHpPct = Math.max(0, Math.min(100, (mobHp / mobMax) * 100));

  return (
    <div data-testid="wilds-mob-result">
      {/* HP bars */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="rounded-lg bg-black/60 border border-emerald-900/40 p-2">
          <div className="text-[9px] uppercase tracking-wider text-emerald-400/80">🐦 You</div>
          <div className="flex items-center justify-between text-[10px] mb-0.5">
            <span className="text-neutral-400">HP</span>
            <span className="font-mono text-emerald-300">{Math.round(petHp)} / {petMax}</span>
          </div>
          <div className="w-full h-2 rounded-full bg-black/60 border border-emerald-900/40 overflow-hidden">
            <div className={`h-full transition-all duration-500 ${
              petHpPct > 50 ? "bg-gradient-to-r from-emerald-700 to-emerald-400" :
              petHpPct > 20 ? "bg-gradient-to-r from-amber-700 to-amber-400" :
              "bg-gradient-to-r from-red-800 to-red-500"
            }`}
                 style={{ width: `${petHpPct}%` }} />
          </div>
        </div>
        <div className="rounded-lg bg-black/60 border border-red-900/40 p-2">
          <div className="text-[9px] uppercase tracking-wider text-red-400/80">{mob.icon} {mob.name}</div>
          <div className="flex items-center justify-between text-[10px] mb-0.5">
            <span className="text-neutral-400">HP</span>
            <span className="font-mono text-red-300">{Math.round(mobHp)} / {mobMax}</span>
          </div>
          <div className="w-full h-2 rounded-full bg-black/60 border border-red-900/40 overflow-hidden">
            <div className="h-full transition-all duration-500 bg-gradient-to-r from-red-800 via-red-500 to-red-300"
                 style={{ width: `${mobHpPct}%` }} />
          </div>
        </div>
      </div>

      {/* Result banner — gated until reveal completes */}
      {!playing && (
        <>
          <div className={`text-center text-3xl font-black mb-1 ${w === "pet" ? "text-amber-300" : "text-red-400"}`}
               style={{ fontFamily: "Anton, sans-serif" }}>
            {w === "pet" ? "🏆 VICTORY" : "💀 DEFEAT"}
          </div>
          <div className="text-xs text-center text-neutral-500 mb-3">
            Resolved in {result.turns} turn{result.turns === 1 ? "" : "s"}
          </div>
          {drops && (
            <div className="rounded-lg bg-amber-950/40 border border-amber-700/50 p-3 mb-3 text-sm" data-testid="wilds-mob-drops">
              <div className="font-black text-amber-200 mb-2">🎁 LOOT</div>
              <div className="space-y-1">
                <div>🪙 <span className="font-bold text-amber-200">{drops.chits}</span> Chits
                  {drops.to_grainhouse > 0 && <span className="text-[10px] text-neutral-500"> · {drops.to_grainhouse} → 🌾 Safe</span>}
                </div>
                {drops.pet_xp > 0 && (
                  <div>🐦 <span className="font-bold text-purple-200">+{drops.pet_xp}</span> Pet XP</div>
                )}
                {drops.material && (
                  <div>
                    {drops.material.icon}{" "}
                    <span className="font-bold text-cyan-200">{drops.material.name}</span>
                    <span className="text-[10px] text-neutral-500"> · ×1</span>
                  </div>
                )}
                {drops.gear && (
                  <div>
                    🎁 <span className="font-bold text-pink-200">{drops.gear.name}</span>
                    <span className="text-[10px] text-neutral-500"> · {drops.gear.rarity}</span>
                  </div>
                )}
                {drops.inner_light_crystals > 0 && (
                  <div data-testid="wilds-drop-inner-light">
                    🌠 <span className="font-bold text-cyan-200">+{drops.inner_light_crystals} Inner Light Crystal{drops.inner_light_crystals > 1 ? "s" : ""}</span>
                    <span className="text-[10px] text-neutral-500"> · rare drop</span>
                  </div>
                )}
                {drops.group_tier_bonus > 0 && (
                  <div className="text-[10px] text-purple-300 italic mt-1">
                    🤝 Group +{drops.group_tier_bonus} tier upgrade
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Battle log reel */}
      <div className="rounded-lg bg-black/70 border border-amber-900/40 p-2 max-h-48 overflow-y-auto text-[11px] leading-relaxed font-mono mb-2"
           data-testid="wilds-battle-log-reel">
        {log.slice(0, revealed).map((entry, i) => (
          <div key={i}
               className={`py-0.5 border-b border-neutral-900/40 last:border-0 ${
                 entry.crit ? "text-amber-200 font-bold" :
                 entry.type === "ko" ? "text-red-300 font-bold" :
                 entry.type === "victory" ? "text-amber-300 font-bold" :
                 "text-neutral-300"
               }`}>
            T{entry.turn || 0}: {entry.msg || entry.type}
          </div>
        ))}
        {playing && (
          <div className="py-0.5 text-amber-400/60 italic animate-pulse">⚔️ …</div>
        )}
      </div>

      <div className="flex gap-2">
        {playing && (
          <button
            onClick={skipToEnd}
            data-testid="wilds-replay-skip"
            className="flex-1 py-2 rounded-lg bg-purple-700 hover:bg-purple-600 text-white font-bold text-xs"
          >
            ⏭ Skip to End
          </button>
        )}
        <button
          onClick={onClose}
          className="flex-1 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-amber-200 font-bold text-sm"
          data-testid="wilds-replay-close"
        >
          Close
        </button>
      </div>
    </div>
  );
}



/* ───────── 👥 Wilds Hunting Party V1 ───────── */

function PartyPanel({ party, invites, busy, onCreate, onLeave, onDissolve, onKick, onAccept, onDecline, onInviteClick }) {
  // Show pending invites even when not in a party
  const hasInvites = Array.isArray(invites) && invites.length > 0;
  return (
    <div
      className="relative rounded-2xl border border-indigo-700/50 bg-gradient-to-br from-[#0a0a18] via-black to-[#0d0820] p-5 mb-5"
      data-testid="wilds-party-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-indigo-400/80">Hunting Party</div>
          <h2 className="text-2xl md:text-3xl font-black text-indigo-200" style={{ fontFamily: "Anton, sans-serif" }}>
            👥 {party ? party.name : "FORM A PARTY"}
          </h2>
          <div className="text-xs text-neutral-400 mt-1">
            {party
              ? `${party.member_count} / ${party.max_members} heroes · Cari's Group Combat tier-upgrade active`
              : "Hunt the Wilds with up to 2 friends. Tier-upgraded loot when 2-3 strong."}
          </div>
        </div>
        {!party && (
          <button
            onClick={onCreate}
            disabled={busy}
            data-testid="wilds-party-create"
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-bold text-sm"
          >
            🐦 Form Party
          </button>
        )}
        {party && (
          <div className="flex gap-2 flex-wrap">
            {party.member_count < party.max_members && party.members.some((m) => m.is_viewer && m.role === "leader") && (
              <button
                onClick={onInviteClick}
                disabled={busy}
                data-testid="wilds-party-invite"
                className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold"
              >
                ➕ Invite Friend
              </button>
            )}
            <button
              onClick={onLeave}
              disabled={busy}
              data-testid="wilds-party-leave"
              className="px-3 py-1.5 rounded-md bg-zinc-700 hover:bg-zinc-600 text-amber-200 text-xs font-bold"
            >
              Leave
            </button>
            {party.members.some((m) => m.is_viewer && m.role === "leader") && party.member_count > 1 && (
              <button
                onClick={onDissolve}
                disabled={busy}
                data-testid="wilds-party-dissolve"
                className="px-3 py-1.5 rounded-md bg-red-700 hover:bg-red-600 text-white text-xs font-bold"
              >
                💀 Dissolve
              </button>
            )}
          </div>
        )}
      </div>

      {party && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
          {party.members.map((m) => (
            <div
              key={m.user_id}
              className={`rounded-lg p-3 text-xs border ${
                m.is_viewer
                  ? "bg-indigo-950/60 border-indigo-500/60"
                  : "bg-black/50 border-indigo-900/40"
              }`}
              data-testid={`wilds-party-member-${m.user_id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-bold text-indigo-100 truncate">
                  {m.role === "leader" ? "👑 " : "🐦 "}
                  {m.is_viewer ? "You" : m.user_id.slice(-8)}
                </span>
                {party.members.some((x) => x.is_viewer && x.role === "leader") && !m.is_viewer && (
                  <button
                    onClick={() => onKick(m.user_id)}
                    disabled={busy}
                    className="text-[10px] text-red-400 hover:text-red-200 font-bold"
                    title="Kick this member"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">{m.role}</div>
            </div>
          ))}
        </div>
      )}

      {/* Pending invites (only when NOT in another party) */}
      {!party && hasInvites && (
        <div className="space-y-2 mt-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Pending Invites</div>
          {invites.map((inv) => (
            <div
              key={inv.party_id}
              className="flex items-center justify-between gap-2 rounded-lg bg-emerald-950/40 border border-emerald-700/40 p-2"
              data-testid={`wilds-party-invite-${inv.party_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-emerald-200 truncate">
                  {inv.party_name} · 👑 {inv.leader_name}
                </div>
                <div className="text-[10px] text-neutral-500">
                  {inv.member_count}/{inv.max_members} heroes
                </div>
              </div>
              <button
                onClick={() => onAccept(inv.party_id)}
                disabled={busy}
                className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold"
                data-testid={`wilds-party-invite-accept-${inv.party_id}`}
              >
                ✓ Accept
              </button>
              <button
                onClick={() => onDecline(inv.party_id)}
                disabled={busy}
                className="px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 text-amber-200 text-[10px] font-bold"
              >
                ✕ Decline
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function InviteFriendModal({ party, onClose, onInvited }) {
  const [friends, setFriends] = useState([]);
  const [busy, setBusy] = useState(false);
  // 🌐 Open-search for non-friends — canon Feb 2026: anyone can invite anyone.
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/friends`);
        if (alive) setFriends(r.data?.friends || []);
      } catch (e) {
        toast.error("Couldn't load friends list.");
      }
    })();
    return () => { alive = false; };
  }, []);

  // Debounced user-search across the entire realm — fires when the
  // player types ≥ 2 chars in the search input.
  useEffect(() => {
    const q = (searchQ || "").trim();
    if (q.length < 2) { setSearchResults([]); return; }
    let alive = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/users/search`, { params: { q } });
        if (alive) setSearchResults(r.data?.results || []);
      } catch {
        if (alive) setSearchResults([]);
      } finally {
        if (alive) setSearching(false);
      }
    }, 280);
    return () => { alive = false; clearTimeout(t); };
  }, [searchQ]);

  const partyMemberIds = new Set((party?.members || []).map((m) => m.user_id));
  const invitedIds = new Set((party?.pending_invites || []).map((p) => p.user_id));
  const eligibleFriends = friends.filter(
    (f) => !partyMemberIds.has(f.user_id) && !invitedIds.has(f.user_id)
  );
  const eligibleSearch = searchResults.filter(
    (u) => !partyMemberIds.has(u.user_id) && !invitedIds.has(u.user_id)
  );

  const onInvite = async (friend_id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/party/invite`, { friend_id });
      toast.success("📨 Invite sent!");
      onInvited?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="wilds-party-invite-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl border border-indigo-700/50 bg-gradient-to-br from-[#0a0a18] to-black p-5"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-red-500/30 flex items-center justify-center text-red-300 text-lg"
        >
          ×
        </button>
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-indigo-300/80">Invite Anyone</div>
          <h3 className="text-xl font-black text-indigo-200" style={{ fontFamily: "Anton, sans-serif" }}>
            ➕ ADD TO PARTY
          </h3>
          <div className="text-[11px] text-neutral-400 mt-1 italic">
            No friend gate — invite anyone by name or handle.
          </div>
        </div>

        {/* 🔍 Open search — any Phoenix in the realm */}
        <div className="mb-4">
          <input
            data-testid="wilds-party-invite-search"
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="🔍 Search by name or handle…"
            className="w-full bg-black/60 border border-indigo-700/40 rounded-lg px-3 py-2 text-sm text-indigo-100 placeholder:text-neutral-500 focus:outline-none focus:border-indigo-400"
          />
          {searchQ.trim().length >= 2 && (
            <div className="mt-2 space-y-2" data-testid="wilds-party-invite-search-results">
              {searching && (
                <div className="text-[11px] text-neutral-500 italic p-2">Searching the realm…</div>
              )}
              {!searching && eligibleSearch.length === 0 && (
                <div className="text-[11px] text-neutral-500 italic p-2">
                  No matching heroes — try a different name.
                </div>
              )}
              {eligibleSearch.map((u) => (
                <button
                  key={u.user_id}
                  onClick={() => onInvite(u.user_id)}
                  disabled={busy}
                  data-testid={`wilds-party-invite-search-${u.user_id}`}
                  className="w-full flex items-center justify-between rounded-lg bg-indigo-950/50 hover:bg-indigo-900/50 disabled:opacity-50 p-3 text-left border border-indigo-800/40"
                >
                  <div>
                    <div className="text-sm font-bold text-amber-100">{u.name || u.handle || "Phoenix"}</div>
                    {u.handle && (
                      <div className="text-[10px] text-indigo-300/80">@{u.handle}</div>
                    )}
                  </div>
                  <div className="text-indigo-300 text-xl">→</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 🤝 Quick-pick from friends (shortcut) */}
        {eligibleFriends.length > 0 && (
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            Quick pick — your friends
          </div>
        )}
        {eligibleFriends.length === 0 && searchQ.trim().length < 2 && (
          <div className="text-sm text-neutral-400 italic p-4 text-center">
            {friends.length === 0
              ? "You have no friends yet — use the search above to invite anyone."
              : "All your friends are already in this party (or pending). Use search to invite others."}
          </div>
        )}
        <div className="space-y-2">
          {eligibleFriends.map((f) => (
            <button
              key={f.user_id}
              onClick={() => onInvite(f.user_id)}
              disabled={busy}
              data-testid={`wilds-party-invite-friend-${f.user_id}`}
              className="w-full flex items-center justify-between rounded-lg bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 p-3 text-left"
            >
              <div>
                <div className="text-sm font-bold text-amber-100">{f.name || f.handle || "Phoenix"}</div>
                {f.tier_label && (
                  <div className="text-[10px] text-neutral-500">{f.tier_label}</div>
                )}
              </div>
              <div className="text-indigo-300 text-xl">→</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}



// ───────── 🏰 Mini Castles — embedded panel for the Wilds page ─────────
function WildsCastlesPanel({ snap }) {
  if (!snap) {
    return (
      <div data-testid="wilds-castles-panel-loading"
           className="rounded-2xl border-2 border-amber-700/30 bg-black/40 p-5 animate-pulse">
        <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400/60">🏰 Scouting castles…</div>
      </div>
    );
  }
  const myCastle = snap.state?.castle;
  const neighbors = snap.map?.castles || [];

  return (
    <div
      data-testid="wilds-castles-panel"
      className="rounded-2xl border-2 border-amber-600/40 bg-gradient-to-br from-[#1a0010] via-black/70 to-[#10001a] p-5"
    >
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/80">Drifter Keeps · Mini Castles</div>
          <h3 className="text-xl font-black text-amber-100" style={{ fontFamily: "Anton, sans-serif" }}>
            🏰 MINI CASTLES
          </h3>
        </div>
        <Link
          to="/castle"
          data-testid="wilds-castles-open"
          className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded bg-[#D4AF37] text-black hover:bg-amber-300 transition"
        >
          {myCastle ? "Manage →" : "Stake Yours →"}
        </Link>
      </div>

      {/* My castle pill OR claim CTA */}
      {myCastle ? (
        <Link to="/castle" data-testid="wilds-my-castle" className="block mb-3">
          <div className="rounded-lg border border-amber-500/40 bg-black/50 p-3 flex items-center justify-between hover:border-amber-300/70 transition">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400/70">Your Keep</div>
              <div className="font-bold text-amber-100">{myCastle.name}</div>
              <div className="text-[10px] text-neutral-500">
                {myCastle.location?.quadrant} · ({myCastle.location?.x}, {myCastle.location?.y})
              </div>
            </div>
            <div className="text-right">
              <div className="text-[#D4AF37] font-black">{(myCastle.chit_vault || 0).toLocaleString()} 🪙</div>
              <div className="text-[10px] uppercase tracking-widest">
                {myCastle.bubble_active ? (
                  <span className="text-sky-300">🛡️ Bubbled</span>
                ) : (
                  <span className="text-red-400/80">⚔️ Raidable</span>
                )}
              </div>
            </div>
          </div>
        </Link>
      ) : (
        <Link to="/castle" data-testid="wilds-claim-cta" className="block mb-3">
          <div className="rounded-lg border-2 border-dashed border-amber-500/50 bg-black/40 p-4 text-center hover:border-amber-300 transition">
            <div className="text-2xl mb-1">🏗️</div>
            <div className="font-bold text-amber-100 text-sm">Claim your castle on the Wilds map</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Tap to stake your keep · 1 per drifter</div>
          </div>
        </Link>
      )}

      {/* Neighbour castles (max 6 shown — drill into /castle for full map) */}
      {neighbors.length > 0 ? (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            🗡️ Raidable Drifters Nearby ({snap.map.total})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {neighbors.slice(0, 6).map((c) => (
              <Link
                key={c.owner_user_id}
                to="/castle"
                data-testid={`wilds-raid-target-${c.owner_user_id}`}
                className="block rounded-lg border border-purple-800/50 bg-black/50 p-2.5 hover:border-purple-500 transition"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-amber-100 truncate">{c.name}</div>
                    <div className="text-[10px] text-neutral-500">@{c.owner_handle}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[#D4AF37] text-xs font-bold">
                      {(c.chit_vault || 0).toLocaleString()} 🪙
                    </div>
                    {c.bubble_active && (
                      <div className="text-[9px] text-sky-300">🛡️ Bubbled</div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-3 text-neutral-500 text-xs" data-testid="wilds-castles-empty">
          No drifters have claimed castles here yet. Be the first.
        </div>
      )}
    </div>
  );
}
